/**
 * src/pages/AccountSettings.tsx
 *
 * ★ force_change_password=1 対応:
 *   - URL パラメータを検出して強制変更モードに切り替え
 *   - 強制時: 警告バナー表示・メール変更フォーム非表示・成功後ホームへリダイレクト
 *   - 初期PW入力欄に自動フォーカス
 *
 * ★ モバイル対応（既存）:
 *   - 入力欄 font-size: 16px（iOS自動ズーム防止）
 *   - ボタン height: 48px（タッチしやすく）
 */

import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../components/AuthGuard'
import { useIsMobile } from '../hooks/useIsMobile'
import SecuritySettings from '../components/mfa/SecuritySettings'
import { METHOD_LABEL, type MfaMethod } from '../api/mfa'

const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://mangaos.studio-nekonomiya.workers.dev/api'

async function apiFetch<T>(path: string, method: 'GET' | 'PATCH' | 'POST', body?: object): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error((data as any).error ?? 'エラーが発生しました')
  return data as T
}

function ResultBanner({ result }: { result: { ok: boolean; msg: string } | null }) {
  if (!result) return null
  return (
    <div style={{
      padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13,
      background: result.ok ? 'var(--green-light)' : 'var(--red-light)',
      color: result.ok ? 'var(--green)' : 'var(--red)',
      border: `1px solid ${result.ok ? 'var(--green-border)' : 'var(--red-border)'}`,
    }}>
      {result.ok ? '✓ ' : '⚠ '}{result.msg}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// メールアドレス変更フォーム（強制変更時は非表示）
// ────────────────────────────────────────────────────────────────
function EmailChangeForm({ isMobile }: { isMobile: boolean }) {
  const { user } = useAuth()
  const [newEmail, setNewEmail] = useState('')
  const [password, setPassword] = useState('')
  const [saving,   setSaving]   = useState(false)
  const [result,   setResult]   = useState<{ ok: boolean; msg: string } | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newEmail.includes('@')) { setResult({ ok: false, msg: '有効なメールアドレスを入力してください' }); return }
    if (newEmail.toLowerCase().trim() === user?.email?.toLowerCase()) {
      setResult({ ok: false, msg: '現在と同じメールアドレスです' }); return
    }
    setSaving(true); setResult(null)
    try {
      await apiFetch('/auth/account', 'PATCH', { email: newEmail, current_password: password })
      setResult({ ok: true, msg: 'メールアドレスを変更しました。' })
      setNewEmail('')
      setPassword('')
      setTimeout(() => window.location.reload(), 1500)
    } catch (err: any) {
      setResult({ ok: false, msg: err.message })
    } finally { setSaving(false) }
  }

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header"><span className="card-title">メールアドレス変更</span></div>
      <div style={{ padding: isMobile ? '16px 16px' : '20px 24px' }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 4 }}>現在のメールアドレス</div>
          <div style={{ fontSize: 14, fontWeight: 500, fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>
            {user?.email ?? '—'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.7, marginTop: 6 }}>
            メールアドレスは<strong>ログインID</strong>です。変更すると、次回から新しいアドレスでログインします（他の端末のログインは外れます）。
          </div>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group" style={{ marginBottom: 16 }}>
            <label>新しいメールアドレス</label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="new@example.com"
              autoComplete="email"
              required
              disabled={saving}
              style={{ fontSize: 16, height: isMobile ? 46 : undefined }}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 16 }}>
            <label>現在のパスワード（本人確認）</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              disabled={saving}
              style={{ fontSize: 16, height: isMobile ? 46 : undefined }}
            />
          </div>
          <ResultBanner result={result} />
          <button type="submit" className="btn btn-primary" disabled={saving || !newEmail || !password}
            style={{ width: isMobile ? '100%' : undefined, height: isMobile ? 48 : undefined }}>
            {saving ? '変更中...' : 'メールアドレスを変更'}
          </button>
        </form>
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// アシスタント: メールアドレス（ログインID）。確認・変更は「ログイン・セキュリティ」（確認コード付き）へ
// ────────────────────────────────────────────────────────────────
function AssistantEmailCard({ isMobile }: { isMobile: boolean }) {
  const { user } = useAuth()
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header"><span className="card-title">メールアドレス（ログインID）</span></div>
      <div style={{ padding: isMobile ? '16px 16px' : '20px 24px' }}>
        <div style={{ fontSize: 14, fontWeight: 500, fontFamily: 'var(--font-mono)', wordBreak: 'break-all', marginBottom: 8 }}>
          {user?.email ?? '—'}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.8, marginBottom: 12 }}>
          メールアドレスは<strong>ログインID</strong>です。確認・変更は、確認コードで本人のアドレスだと確かめてから行います。
          変更すると、次回から新しいアドレスでログインします（他の端末のログインは外れます）。
        </div>
        <Link to="/account/security" className="btn btn-outline"
          style={{ width: isMobile ? '100%' : undefined, boxSizing: 'border-box', textAlign: 'center', textDecoration: 'none' }}>
          メールアドレスを確認・変更する ›
        </Link>
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// パスワード変更フォーム
// ★ forcedChange=true のとき: 自動フォーカス + 成功後ホームへリダイレクト
// ────────────────────────────────────────────────────────────────
function PasswordChangeForm({
  isMobile,
  forcedChange = false,
}: {
  isMobile:      boolean
  forcedChange?: boolean
}) {
  const [form, setForm] = useState({
    current_password: '',
    new_password:     '',
    confirm_password: '',
  })
  const [saving, setSaving] = useState(false)
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null)
  const [showPw, setShowPw] = useState(false)

  // ★ 強制変更時: 最初の入力欄に自動フォーカス
  const firstInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (!forcedChange) return undefined
  
      const t = setTimeout(() => firstInputRef.current?.focus(), 100)
  return () => clearTimeout(t)
  }, [forcedChange])

  const set = (k: keyof typeof form, v: string) => {
    setForm((f) => ({ ...f, [k]: v }))
    setResult(null)
  }

  const validate = (): string | null => {
    if (!form.current_password) return '現在のパスワードを入力してください'
    if (!form.new_password)     return '新しいパスワードを入力してください'
    if (form.new_password.length < 8) return '新しいパスワードは8文字以上にしてください'
    if (form.new_password !== form.confirm_password) return 'パスワードが一致しません'
    if (form.current_password === form.new_password) return '現在のパスワードと同じです'
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const err = validate()
    if (err) { setResult({ ok: false, msg: err }); return }
    setSaving(true); setResult(null)
    try {
      await apiFetch('/auth/change-password', 'POST', {
        current_password: form.current_password,
        new_password:     form.new_password,
      })

      const successMsg = forcedChange
        ? 'パスワードを変更しました。ホーム画面に移動します...'
        : 'パスワードを変更しました。'
      setResult({ ok: true, msg: successMsg })
      setForm({ current_password: '', new_password: '', confirm_password: '' })

      // ★ 強制変更完了 → 1.5秒後にホームへリダイレクト
      if (forcedChange) {
        setTimeout(() => { window.location.replace('/') }, 1500)
      }
    } catch (err: any) {
      setResult({ ok: false, msg: err.message })
    } finally { setSaving(false) }
  }

  const inputStyle: React.CSSProperties = {
    fontSize: 16,
    height:   isMobile ? 46 : undefined,
    fontFamily: 'monospace',
  }

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">パスワード変更</span>
      </div>
      <div style={{ padding: isMobile ? '16px 16px' : '20px 24px' }}>
        <form onSubmit={handleSubmit}>

          {/* 現在のパスワード */}
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label>
              現在のパスワード
              {/* ★ 強制変更時: 初期パスワードのヒントを表示 */}
              {forcedChange && (
                <span style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 6, fontWeight: 400 }}>
                  （管理者から受け取った初期パスワード）
                </span>
              )}
            </label>
            <input
              ref={firstInputRef}
              type={showPw ? 'text' : 'password'}
              value={form.current_password}
              onChange={(e) => set('current_password', e.target.value)}
              autoComplete="current-password"
              required
              disabled={saving}
              style={inputStyle}
            />
          </div>

          {/* 新しいパスワード */}
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label>
              新しいパスワード
              <span style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 6, fontWeight: 400 }}>
                （8文字以上）
              </span>
            </label>
            <input
              type={showPw ? 'text' : 'password'}
              value={form.new_password}
              onChange={(e) => set('new_password', e.target.value)}
              autoComplete="new-password"
              required
              disabled={saving}
              style={{
                ...inputStyle,
                borderColor: form.new_password.length > 0 && form.new_password.length < 8
                  ? 'var(--red)'
                  : undefined,
              }}
            />
            {form.new_password.length > 0 && form.new_password.length < 8 && (
              <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 3 }}>
                あと{8 - form.new_password.length}文字必要です
              </div>
            )}
          </div>

          {/* 確認入力 */}
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label>新しいパスワード（確認）</label>
            <input
              type={showPw ? 'text' : 'password'}
              value={form.confirm_password}
              onChange={(e) => set('confirm_password', e.target.value)}
              autoComplete="new-password"
              required
              disabled={saving}
              style={{
                ...inputStyle,
                borderColor: form.confirm_password && form.confirm_password !== form.new_password
                  ? 'var(--red)'
                  : undefined,
              }}
            />
            {form.confirm_password && form.confirm_password !== form.new_password && (
              <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 3 }}>
                パスワードが一致しません
              </div>
            )}
          </div>

          {/* パスワード表示切り替え */}
          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: 'flex', alignItems: 'center',
              gap: 8, cursor: 'pointer', fontSize: 13,
            }}>
              <input
                type="checkbox"
                checked={showPw}
                onChange={(e) => setShowPw(e.target.checked)}
                style={{ width: 18, height: 18 }}
              />
              パスワードを表示
            </label>
          </div>

          <ResultBanner result={result} />

          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving || !form.current_password || !form.new_password || !form.confirm_password}
            style={{
              width:  isMobile ? '100%' : undefined,
              height: isMobile ? 48     : undefined,
            }}
          >
            {saving ? '変更中...' : 'パスワードを変更'}
          </button>
        </form>
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// 「ログイン・セキュリティ」への入口（追加認証の設定・パスキー・メール認証はそちらにまとめた）
// ────────────────────────────────────────────────────────────────
function LoginSecurityLink({ isMobile, method }: { isMobile: boolean; method?: MfaMethod }) {
  const m: MfaMethod = method === 'passkey' || method === 'email' ? method : 'none'
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header"><span className="card-title">ログイン・セキュリティ</span></div>
      <div style={{ padding: isMobile ? '16px 16px' : '20px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>追加認証</span>
          {m === 'none' ? <span className="badge badge-amber">未設定</span> : <span className="badge badge-green">{METHOD_LABEL[m]}</span>}
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.8, marginBottom: 12 }}>
          パスワードに加えて本人確認をする「追加認証」の設定や、パスキーの管理はこちらです。
        </div>
        <Link to="/account/security" className="btn btn-outline"
          style={{ width: isMobile ? '100%' : undefined, boxSizing: 'border-box', textAlign: 'center', textDecoration: 'none' }}>
          ログイン・セキュリティを開く ›
        </Link>
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// メインページ
// ────────────────────────────────────────────────────────────────
export default function AccountSettings() {
  const { user, isAdmin, logout } = useAuth()
  const isMobile = useIsMobile()

  // ★ URL パラメータから強制変更フラグを検出
  //    client.ts の 403 ハンドラ・Login.tsx のリダイレクト先 と対になる
  const isForced =
    new URLSearchParams(window.location.search).get('force_change_password') === '1'

  // ★ 追加認証の初期設定が必要（必須化ON・未設定／管理者の復旧後）。設定が終わるまで他の画面には進めない
  const isMfaSetup =
    !isForced && (new URLSearchParams(window.location.search).get('mfa_setup') === '1' || !!user?.mfa_setup_required)

  // メール認証を使っているユーザーは、確認コード付きのメール変更を使う（旧フォームはサーバーが拒否する）
  const hideLegacyEmailForm = user?.mfa_method === 'email'

  if (isMfaSetup) {
    return (
      <div style={{ maxWidth: 560 }}>
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: isMobile ? 17 : 18, fontWeight: 700, margin: 0, marginBottom: 4 }}>
            追加認証の設定
          </h2>
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            {isAdmin ? '管理者アカウント' : 'アシスタントアカウント'}
            {user?.assistant_name && ` — ${user.assistant_name}`}
          </div>
        </div>
        <SecuritySettings isMobile={isMobile} forceSetup />
        <button className="btn btn-ghost btn-sm" onClick={() => void logout()}>ログアウト</button>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 560 }}>

      {/* ★ 強制変更バナー */}
      {isForced && (
        <div style={{
          background: 'var(--amber-light)',
          border:     '1px solid rgba(176,122,18,0.35)',
          borderRadius: 10,
          padding:    '16px 18px',
          marginBottom: 24,
          display:    'flex',
          gap:        12,
          alignItems: 'flex-start',
        }}>
          <span style={{ fontSize: 22, lineHeight: 1.2, flexShrink: 0 }}>🔐</span>
          <div>
            <div style={{
              fontWeight: 700, color: 'var(--amber)',
              fontSize: 14, marginBottom: 6,
            }}>
              初期パスワードの変更が必要です
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.6 }}>
              セキュリティのため、最初のログイン時にパスワードを変更してください。
              管理者から受け取った初期パスワードを入力し、新しいパスワードに変更してください。
              変更後、自動的にホーム画面に移動します。
            </div>
          </div>
        </div>
      )}

      {/* ヘッダー（通常時のみ） */}
      {!isForced && (
        <div style={{ marginBottom: 20 }}>
          <h2 style={{
            fontSize: isMobile ? 17 : 18,
            fontWeight: 700, margin: 0, marginBottom: 4,
          }}>
            アカウント設定
          </h2>
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            {isAdmin ? '管理者アカウント' : 'アシスタントアカウント'}
            {user?.assistant_name && ` — ${user.assistant_name}`}
          </div>
        </div>
      )}

      {/* メールアドレス（強制変更中は非表示）。アシスタントは確認コード付きの「ログイン・セキュリティ」に一本化し、
          確認なしで書き換える旧フォームは出さない。管理者は従来のフォーム */}
      {!isForced && isAdmin && !hideLegacyEmailForm && <EmailChangeForm isMobile={isMobile} />}
      {!isForced && !isAdmin && <AssistantEmailCard isMobile={isMobile} />}

      {/* パスワード変更フォーム（常に表示） */}
      <div style={{ marginBottom: 20 }}>
        <PasswordChangeForm isMobile={isMobile} forcedChange={isForced} />
      </div>

      {/* ログインの追加認証は「ログイン・セキュリティ」へ（強制PW変更中は非表示） */}
      {!isForced && <LoginSecurityLink isMobile={isMobile} method={user?.mfa_method as MfaMethod | undefined} />}

      {/* フッターノート（強制変更中は非表示） */}
      {!isForced && (
        <div style={{
          fontSize: 11, color: 'var(--ink-3)',
          padding: '10px 14px', background: 'var(--paper-2)',
          borderRadius: 6, marginTop: 8,
        }}>
          💡 変更後もセッションは維持されます。次回ログインから新しい情報が有効になります。
        </div>
      )}
    </div>
  )
}